import { useCallback, useState, useEffect, useRef } from 'react';
import { useCardContext } from '../../../context/CardContext';
import useElementWidthObserver from '../../../hooks/useElementWidthObserver';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AssociationsSortMoreButton from './AssociationsSortMoreButton';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { ASSOCIATION_LABEL_SORT_KEY } from '../../../constants/associationLabelSortKey';
import DynamicWidthSelect from './DynamicWidthSelect';
import DynamicWidthText from './DynamicWidthText';
import { createSortConfiguration } from '../../../utils/createSortConfiguration';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CONTAINER_WIDTH_THRESHOLD = 240;
function AssociationSortButtons({
  propertyDefinitions
}) {
  var _ref, _firstSort$property, _firstSort$order;
  const {
    sortConfiguration,
    onSortConfigurationChange,
    userPermission,
    propertyNames,
    objectTypeDefinition,
    toObjectTypeId
  } = useCardContext();
  const firstSort = sortConfiguration === null || sortConfiguration === void 0 ? void 0 : sortConfiguration[0];
  const currentSortPropertyName = (_ref = (_firstSort$property = firstSort === null || firstSort === void 0 ? void 0 : firstSort.property) !== null && _firstSort$property !== void 0 ? _firstSort$property : objectTypeDefinition === null || objectTypeDefinition === void 0 ? void 0 : objectTypeDefinition.createDatePropertyName) !== null && _ref !== void 0 ? _ref : 'hs_object_id';
  const currentSortOrder = (_firstSort$order = firstSort === null || firstSort === void 0 ? void 0 : firstSort.order) !== null && _firstSort$order !== void 0 ? _firstSort$order : 'DESC';
  const [sortPropertyOptions, setSortPropertyOptions] = useState([]);
  const [showMoreButton, setShowMoreButton] = useState(false);
  const containerRef = useRef(null);
  const actionBarRef = useRef(null);
  useEffect(() => {
    var _containerRef$current;
    if ((_containerRef$current = containerRef.current) !== null && _containerRef$current !== void 0 && _containerRef$current.parentElement) {
      actionBarRef.current = containerRef.current.parentElement;
    }
  }, []);
  const actionBarWidth = useElementWidthObserver(actionBarRef);
  useEffect(() => {
    const shouldShowMore = actionBarWidth > 0 && actionBarWidth < CONTAINER_WIDTH_THRESHOLD;
    setShowMoreButton(shouldShowMore);
  }, [actionBarWidth]);
  const handlePropertyChange = useCallback(value => {
    const standardizedSorts = createSortConfiguration(value.currentTarget.value, currentSortOrder);
    onSortConfigurationChange(standardizedSorts);
  }, [onSortConfigurationChange, currentSortOrder]);
  const handleOrderChange = useCallback(value => {
    const standardizedSorts = createSortConfiguration(currentSortPropertyName, value.currentTarget.value);
    onSortConfigurationChange(standardizedSorts);
  }, [onSortConfigurationChange, currentSortPropertyName]);
  const getPropertyLabel = useCallback(propertyName => {
    const propertyDef = propertyDefinitions === null || propertyDefinitions === void 0 ? void 0 : propertyDefinitions.find(def => def.property.name === propertyName);
    return (propertyDef === null || propertyDef === void 0 ? void 0 : propertyDef.property.label) || propertyName;
  }, [propertyDefinitions]);
  const getRequiredPropertyNames = useCallback(() => {
    const requiredProperties = [];
    if (toObjectTypeId === CONTACT_TYPE_ID) {
      requiredProperties.push('firstname', 'lastname');
    } else if (objectTypeDefinition !== null && objectTypeDefinition !== void 0 && objectTypeDefinition.primaryDisplayLabelPropertyName) {
      requiredProperties.push(objectTypeDefinition.primaryDisplayLabelPropertyName);
    }
    if (objectTypeDefinition !== null && objectTypeDefinition !== void 0 && objectTypeDefinition.createDatePropertyName) {
      requiredProperties.push(objectTypeDefinition.createDatePropertyName);
    }
    requiredProperties.push(ASSOCIATION_LABEL_SORT_KEY);
    return requiredProperties;
  }, [objectTypeDefinition, toObjectTypeId]);
  const getSortPropertyOptions = useCallback(() => {
    if (!propertyNames) {
      return [];
    }
    const requiredPropertyNames = getRequiredPropertyNames();
    const sortPropertyNames = Array.from(new Set([...propertyNames, ...requiredPropertyNames]));
    const propertyOptions = sortPropertyNames.map(name => {
      if (name === ASSOCIATION_LABEL_SORT_KEY) {
        return {
          text: I18n.text('cardAssociatedObjects.AssociatedObjectsActionBar.associationLabel'),
          value: ASSOCIATION_LABEL_SORT_KEY
        };
      }
      return {
        text: getPropertyLabel(name),
        value: name
      };
    });
    return propertyOptions;
  }, [propertyNames, getRequiredPropertyNames, getPropertyLabel]);
  useEffect(() => {
    const options = getSortPropertyOptions();
    setSortPropertyOptions(options);
  }, [getSortPropertyOptions]);
  const sortOrderOptions = [{
    text: I18n.text('cardAssociatedObjects.AssociatedObjectsActionBar.ASC'),
    value: 'ASC'
  }, {
    text: I18n.text('cardAssociatedObjects.AssociatedObjectsActionBar.DESC'),
    value: 'DESC'
  }];
  const getOrderText = useCallback(() => {
    if (currentSortOrder === 'ASC') {
      return I18n.text('cardAssociatedObjects.AssociatedObjectsActionBar.ASC');
    }
    return I18n.text('cardAssociatedObjects.AssociatedObjectsActionBar.DESC');
  }, [currentSortOrder]);
  if (!userPermission) {
    const propertyText = getPropertyLabel(currentSortPropertyName);
    const orderText = getOrderText();
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      alignItems: "center",
      gap: "100",
      children: [/*#__PURE__*/_jsx(View, {
        flexShrink: 0,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "cardAssociatedObjects.AssociatedObjectsActionBar.sortedBy"
        })
      }), /*#__PURE__*/_jsx(DynamicWidthText, {
        text: propertyText,
        dataTestId: "readonly-sort-property-text"
      }), /*#__PURE__*/_jsx(Text, {
        children: "|"
      }), /*#__PURE__*/_jsx(DynamicWidthText, {
        text: orderText,
        dataTestId: "readonly-sort-order-text"
      })]
    });
  }
  const selectMenu = /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    gap: "300",
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(DynamicWidthSelect, {
      value: currentSortPropertyName,
      onChange: handlePropertyChange,
      options: sortPropertyOptions,
      dataTestId: "sort-property-select"
    }), /*#__PURE__*/_jsx(DynamicWidthSelect, {
      value: currentSortOrder.toString(),
      onChange: handleOrderChange,
      options: sortOrderOptions,
      dataTestId: "sort-order-select"
    })]
  });
  return /*#__PURE__*/_jsx(View, {
    ref: containerRef,
    "data-test-id": "associations-sort-buttons-container",
    maxBlockSize: "calc(100% - 35px)",
    overflow: "hidden",
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      alignItems: "center",
      gap: "100",
      children: [/*#__PURE__*/_jsx(View, {
        flexShrink: 0,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "cardAssociatedObjects.AssociatedObjectsActionBar.sortBy"
        })
      }), !showMoreButton ? selectMenu : /*#__PURE__*/_jsx(AssociationsSortMoreButton, {
        selectMenu: selectMenu
      })]
    })
  });
}
export default AssociationSortButtons;